import React, { useState, useRef } from 'react';
import { Palette, Type, Image, CreditCard, CheckCircle, Leaf } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const AddCO2Badge = () => {
  const { t } = useLanguage();
  const [badgeConfig, setBadgeConfig] = useState({
    backgroundColor: '#4CAF50',
    textColor: '#FFFFFF',
    fontSize: '14px',
    fontFamily: 'Inter',
    borderRadius: '8px',
    padding: '12px',
    language: 'tr',
    showIcon: true,
    iconColor: '#FFFFFF'
  });

  // const [isPreviewMode, setIsPreviewMode] = useState(true);
  const badgeRef = useRef(null);

  const fontOptions = [
    { name: 'Inter', value: 'Inter' },
    { name: 'Poppins', value: 'Poppins' },
    { name: 'Roboto', value: 'Roboto' },
    { name: 'Open Sans', value: 'Open Sans' },
    { name: 'Montserrat', value: 'Montserrat' }
  ];

  const languageOptions = [
    { code: 'tr', name: 'Türkçe', text: 'Bu site karbon nötrdür' },
    { code: 'en', name: 'English', text: 'This site is carbon neutral' },
    { code: 'de', name: 'Deutsch', text: 'Diese Website ist CO₂-neutral' },
    { code: 'fr', name: 'Français', text: 'Ce site est neutre en carbone' },
    { code: 'ar', name: 'العربية', text: 'هذا الموقع محايد للكربون' }
  ];

  const getCurrentText = () => {
    const selectedLang = languageOptions.find(lang => lang.code === badgeConfig.language);
    return selectedLang ? selectedLang.text : languageOptions[0].text;
  };

  const handleConfigChange = (key, value) => {
    setBadgeConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDownload = () => {
    // Satın alma sayfasına yönlendir
    window.location.href = '/certificate-form';
  };

  const generateCSS = () => {
    return `
/* CO2 Neutral Badge CSS */
.co2-neutral-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background-color: ${badgeConfig.backgroundColor};
  color: ${badgeConfig.textColor};
  font-family: '${badgeConfig.fontFamily}', sans-serif;
  font-size: ${badgeConfig.fontSize};
  padding: ${badgeConfig.padding};
  border-radius: ${badgeConfig.borderRadius};
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
}

.co2-neutral-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.co2-neutral-badge svg {
  width: 16px;
  height: 16px;
  fill: ${badgeConfig.iconColor};
}
    `;
  };

  const generateHTML = () => {
    return `
<a href="https://co2neutral.com" target="_blank" class="co2-neutral-badge">
  ${badgeConfig.showIcon ? '<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M6 3c5.5 2.5 5.5 10.5 0 13 1.5-5.5-1.5-10.5-7-13z"/><path d="M6 8c3.5 1.5 3.5 7 0 8.5 1-3.5-1-7-4-8.5z"/></svg>' : ''}
  ${getCurrentText()}
</a>
    `;
  };

  return (
    <section className="min-h-screen bg-neutral-50 py-12 pt-32">
      <div className="container-custom max-w-6xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-neutral-800 mb-4">
            {t('badge-page-title')}
          </h1>
          <p className="text-lg text-neutral-600 max-w-2xl mx-auto">
            {t('badge-page-subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Customization Panel */}
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-neutral-800 mb-6 flex items-center gap-2">
              <Palette className="h-6 w-6 text-primary-500" />
              {t('badge-customization-title')}
            </h2>

            <div className="space-y-6">
              {/* Language Selection */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('badge-language-selection')}
                </label>
                <select
                  value={badgeConfig.language}
                  onChange={(e) => handleConfigChange('language', e.target.value)}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {languageOptions.map(lang => (
                    <option key={lang.code} value={lang.code}>{lang.name} - {lang.text}</option>
                  ))}
                </select>
                <p className="text-xs text-neutral-500 mt-1">
                  {t('badge-selected-language')}: <span className="font-medium">{getCurrentText()}</span>
                </p>
              </div>

              {/* Background Color */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('badge-background-color')}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={badgeConfig.backgroundColor}
                    onChange={(e) => handleConfigChange('backgroundColor', e.target.value)}
                    className="w-12 h-12 border border-neutral-300 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={badgeConfig.backgroundColor}
                    onChange={(e) => handleConfigChange('backgroundColor', e.target.value)}
                    className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Text Color */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('badge-text-color')}
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={badgeConfig.textColor}
                    onChange={(e) => handleConfigChange('textColor', e.target.value)}
                    className="w-12 h-12 border border-neutral-300 rounded-lg cursor-pointer"
                  />
                  <input
                    type="text"
                    value={badgeConfig.textColor}
                    onChange={(e) => handleConfigChange('textColor', e.target.value)}
                    className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Font Family */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('badge-font-family')}
                </label>
                <select
                  value={badgeConfig.fontFamily}
                  onChange={(e) => handleConfigChange('fontFamily', e.target.value)}
                  className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  {fontOptions.map(font => (
                    <option key={font.value} value={font.value}>{font.name}</option>
                  ))}
                </select>
              </div>

              {/* Font Size */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('badge-font-size')}: {badgeConfig.fontSize}
                </label>
                <input
                  type="range"
                  min="10"
                  max="24"
                  value={parseInt(badgeConfig.fontSize)}
                  onChange={(e) => handleConfigChange('fontSize', `${e.target.value}px`)}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              {/* Border Radius */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('badge-border-radius')}: {badgeConfig.borderRadius}
                </label>
                <input
                  type="range"
                  min="0"
                  max="20"
                  value={parseInt(badgeConfig.borderRadius)}
                  onChange={(e) => handleConfigChange('borderRadius', `${e.target.value}px`)}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              {/* Padding */}
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-2">
                  {t('badge-padding')}: {badgeConfig.padding}
                </label>
                <input
                  type="range"
                  min="8"
                  max="24"
                  value={parseInt(badgeConfig.padding)}
                  onChange={(e) => handleConfigChange('padding', `${e.target.value}px`)}
                  className="w-full h-2 bg-neutral-200 rounded-lg appearance-none cursor-pointer"
                />
              </div>

              {/* Icon Options */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="showIcon"
                    checked={badgeConfig.showIcon}
                    onChange={(e) => handleConfigChange('showIcon', e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-neutral-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="showIcon" className="text-sm font-medium text-neutral-700">
                    {t('badge-show-icon')}
                  </label>
                </div>
                
                {badgeConfig.showIcon && (
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">
                      {t('badge-icon-color')}
                    </label>
                    <div className="flex items-center gap-3">
                      <input
                        type="color"
                        value={badgeConfig.iconColor}
                        onChange={(e) => handleConfigChange('iconColor', e.target.value)}
                        className="w-12 h-12 border border-neutral-300 rounded-lg cursor-pointer"
                      />
                      <input
                        type="text"
                        value={badgeConfig.iconColor}
                        onChange={(e) => handleConfigChange('iconColor', e.target.value)}
                        className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg text-sm"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Download Button */}
              <button
                onClick={handleDownload}
                className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
              >
                <CreditCard className="h-5 w-5" />
                {t('badge-purchase-button')}
              </button>
            </div>
          </div>

          {/* Preview Panel */}
          <div className="space-y-8">
            {/* Live Preview */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-semibold text-neutral-800 mb-6 flex items-center gap-2">
                <Image className="h-5 w-5 text-primary-500" />
                {t('badge-preview-title')}
              </h3>
              
              <div className="flex justify-center mb-6">
                <div
                  ref={badgeRef}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: badgeConfig.backgroundColor,
                    color: badgeConfig.textColor,
                    fontFamily: `${badgeConfig.fontFamily}, sans-serif`,
                    fontSize: badgeConfig.fontSize,
                    padding: badgeConfig.padding,
                    borderRadius: badgeConfig.borderRadius,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    transition: 'all 0.3s ease'
                  }}
                  className="hover:transform hover:-translate-y-1 hover:shadow-lg"
                >
                  {badgeConfig.showIcon && (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill={badgeConfig.iconColor}>
                      <path d="M6 3c5.5 2.5 5.5 10.5 0 13 1.5-5.5-1.5-10.5-7-13z"/>
                      <path d="M6 8c3.5 1.5 3.5 7 0 8.5 1-3.5-1-7-4-8.5z"/>
                    </svg>
                  )}
                  {getCurrentText()}
                </div>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {t('badge-preview-feature-1')}
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {t('badge-preview-feature-2')}
                </div>
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  {t('badge-preview-feature-3')}
                </div>
              </div>
            </div>

            {/* Code Preview */}
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h3 className="text-xl font-semibold text-neutral-800 mb-6 flex items-center gap-2">
                <Type className="h-5 w-5 text-primary-500" />
                {t('badge-code-title')}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('badge-css-code')}
                  </label>
                  <pre className="bg-neutral-100 p-4 rounded-lg text-sm overflow-x-auto">
                    <code>{generateCSS()}</code>
                  </pre>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-2">
                    {t('badge-html-code')}
                  </label>
                  <pre className="bg-neutral-100 p-4 rounded-lg text-sm overflow-x-auto">
                    <code>{generateHTML()}</code>
                  </pre>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl p-6 text-white">
              <h3 className="text-xl font-semibold mb-4">{t('badge-features-title')}</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <Leaf className="h-5 w-5" />
                  <span>{t('badge-feature-1')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Leaf className="h-5 w-5" />
                  <span>{t('badge-feature-2')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Leaf className="h-5 w-5" />
                  <span>{t('badge-feature-3')}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Leaf className="h-5 w-5" />
                  <span>{t('badge-feature-4')}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AddCO2Badge; 